import styles from './AssistantWindow.module.css';

import type { ReactNode } from 'react';
import type { AssistantSurface } from 'src/types/assistant';

import { Sparkle } from '@phosphor-icons/react';
import React, { useId, useRef, useState, useEffect } from 'react';

import { useIsWide } from 'src/hooks/useMediaQuery';
import { useHydrated } from 'src/hooks/useHydrated';

import { isDismissKey } from 'src/utils/dialogKeys';

import { useAssistantStatus } from 'src/queries';

import { Modal } from 'src/components/Modal';

import { takeReturnToAssistant } from './returnMark';
import { AssistantChat, ASSISTANT_TITLE } from './AssistantPanel';

/**
 * The assistant on a catalog (app#397, spec §3): a pill beside Buy / Rent — "Ask GAC AI" — and the
 * window it opens. The window opens only on that press, never by itself, except when the person
 * comes Back from a car they left the window for (`returnMark`). The catalog keeps its whole first
 * screen for the cars.
 *
 * `pill` goes into the row of Buy / Rent, `window` anywhere in the screen's tree (a dialog), like
 * the filters' `funnel` and `sheet`. Both are the browser's alone: the server renders the catalog
 * without them, and the pill takes no room the cars would move for when it arrives. Where the
 * installation has no model neither is shown.
 */
export const useAssistantEntry = (
  surface: AssistantSurface
): { pill: ReactNode; window: ReactNode } => {
  const [open, setOpen] = useState(false);

  // Back from a car the person left the window for: the window is open again, once.
  useEffect(() => {
    // The history is outside React: it is read once, when the catalog is back on screen.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (takeReturnToAssistant(surface)) setOpen(true);
  }, [surface]);

  return {
    pill: <AssistantPill open={open} onOpen={() => setOpen(true)} />,
    window: <AssistantWindow surface={surface} open={open} onClose={() => setOpen(false)} />,
  };
};

/** Whether the installation has a model — asked once the browser has the page. */
const useAssistantEnabled = (): boolean => {
  const hydrated = useHydrated();
  const status = useAssistantStatus({ enabled: hydrated });

  return hydrated && !!status.data?.enabled;
};

/**
 * "Ask GAC AI": the one action on the catalog's first screen, so the accent with black letters
 * (brand, «Акцент: лайм»). The word AI in it says what the person will talk to (AI Act art. 50).
 * Under 360px the label is "GAC AI" (spec §3, row и): "Ask" is not displayed, so a screen reader
 * reads what is seen.
 */
const AssistantPill = ({ open, onOpen }: { open: boolean; onOpen: () => void }) =>
  useAssistantEnabled() ? (
    <button
      type="button"
      className={styles.pill}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onOpen}
    >
      <Sparkle size={18} weight="fill" aria-hidden />
      <span>
        <span className={styles.ask}>Ask </span>
        {ASSISTANT_TITLE}
      </span>
    </button>
  ) : null;

interface AssistantWindowProps {
  surface: AssistantSurface;
  open: boolean;
  onClose: () => void;
}

/**
 * The window. Below the laptop width it is a sheet from the bottom, almost the whole screen — the
 * dialog the filters' sheet is (`Modal`: modal, Escape, the focus kept inside and handed back).
 * From 1100px it is a panel on the right, 400px wide, over the catalog's right side: not modal —
 * the filters and the cars stay in reach while the assistant answers — with Escape and the focus
 * handed back as a dialog's.
 */
const AssistantWindow = ({ surface, open, onClose }: AssistantWindowProps) => {
  const enabled = useAssistantEnabled();
  const wide = useIsWide();
  const titleId = useId();

  if (!enabled || !open) return null;

  const chat = (
    <AssistantChat surface={surface} titleId={titleId} onClose={onClose} focusField={wide} />
  );

  if (wide) {
    return (
      <SidePanel titleId={titleId} onClose={onClose}>
        {chat}
      </SidePanel>
    );
  }

  return (
    <Modal
      visible
      onClose={onClose}
      icon={<Sparkle size={64} color="white" />}
      label={ASSISTANT_TITLE}
    >
      <div className={styles.sheet}>{chat}</div>
    </Modal>
  );
};

/** The laptop's panel: a dialog that does not hold the page, named by the window's title. */
const SidePanel = ({
  titleId,
  onClose,
  children,
}: {
  titleId: string;
  onClose: () => void;
  children: ReactNode;
}) => {
  const panel = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const element = panel.current;
    // Captured once per open: where the focus goes back when the panel closes — the pill.
    const opener = document.activeElement as HTMLElement | null;

    // A laptop has no keyboard to pop over the page: the field takes the focus, ready for words.
    (element?.querySelector<HTMLElement>('textarea:not([disabled])') ?? element)?.focus({
      preventScroll: true,
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isDismissKey(event) || event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      // Escape belongs to whatever is in front: an open native list, or another dialog over the page.
      const dialog = target?.closest?.('[role="dialog"]') ?? null;

      if (target?.tagName === 'SELECT' || (dialog && dialog !== element)) return;

      event.preventDefault();
      onCloseRef.current();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && opener.isConnected && typeof opener.focus === 'function')
        opener.focus({ preventScroll: true });
    };
  }, []);

  return (
    <aside
      ref={panel}
      className={styles.panel}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      {children}
    </aside>
  );
};
